import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Simple mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

const Dashboard: React.FC = () => {
  const isMobile = useIsMobile();
  
  const [animatedValues, setAnimatedValues] = useState({
    tvl: isMobile ? 24.5 : 0,
    apr: isMobile ? 4.5 : 0,
    price: isMobile ? 0.875 : 0,
    reserve: isMobile ? 24582194 : 0
  });

  useEffect(() => {
    if (!isMobile) {
      // Only animate numbers on desktop
      const targets = { tvl: 24.5, apr: 4.5, price: 0.875, reserve: 24582194 };
      const steps = 60;
      let step = 0;

      const animate = () => {
        step++;
        const progress = Math.min(step / steps, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        setAnimatedValues({
          tvl: targets.tvl * easeOut,
          apr: targets.apr * easeOut,
          price: targets.price * easeOut,
          reserve: targets.reserve * easeOut
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      animate();
    }
  }, [isMobile]);

  // Simplified animation variants - disabled on mobile
  const containerVariants = {
    hidden: { opacity: isMobile ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: isMobile ? { duration: 0 } : { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: isMobile ? 1 : 0, 
      y: isMobile ? 0 : 20 
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: isMobile ? { duration: 0 } : { duration: 0.6, ease: "easeOut" }
    }
  };

  // Mobile-optimized motion component
  const MobileOptimizedMotion = ({ children, className, variants, whileHover, transition, ...props }: any) => {
    if (isMobile) {
      // Return a regular div on mobile
      return <div className={className}>{children}</div>;
    }
    // Return motion.div on desktop
    return (
      <motion.div 
        className={className}
        variants={variants}
        initial="hidden"
        animate="visible"
        whileHover={whileHover}
        transition={transition}
        {...props}
      >
        {children}
      </motion.div>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-6 font-sans">
      {/* Header Section */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl p-4 lg:p-6 bg-gradient-to-br from-slate-800/80 via-blue-900/20 to-slate-900/80 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0">
          <div className="flex-1">
            <h1 className="text-xl lg:text-2xl font-semibold text-white mb-2 lg:mb-1 font-sans leading-tight">
              The First <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">XRP Neobank</span>
            </h1>
            <p className="text-sm text-slate-300 max-w-2xl font-sans leading-relaxed">
              Unlock real utility for your XRP — earn, swap, and spend securely worldwide.
            </p>
          </div>
          
          <MobileOptimizedMotion 
            className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg bg-gradient-to-r from-slate-800/50 to-blue-900/30 border border-blue-500/30 backdrop-blur-sm self-start lg:self-auto"
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)"
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full overflow-hidden bg-slate-700/50 border border-blue-500/30 flex-shrink-0">
                <img src="/images/XRP.png" alt="XRP" className="w-full h-full object-cover" />
              </div>
              <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full overflow-hidden bg-slate-700/50 border border-blue-500/30 flex-shrink-0">
                <img src="/images/RLUSD.png" alt="RLUSD" className="w-full h-full object-cover" />
              </div>
            </div>
            <span className="text-white text-sm font-medium font-sans whitespace-nowrap">XRP / RLUSD</span>
          </MobileOptimizedMotion>
        </div>
      </MobileOptimizedMotion>

      {/* Main Metrics Grid */}
      <MobileOptimizedMotion 
        className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4"
        variants={containerVariants}
      >
        {/* Total Value Locked */}
        <MobileOptimizedMotion 
          className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          variants={cardVariants}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          <div className="mb-3 lg:mb-3">
            <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2 lg:mb-1 font-sans">
              TOTAL VALUE LOCKED
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-xl lg:text-2xl font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent font-sans">
                ${animatedValues.tvl.toFixed(1)}M
              </span>
              <span className="text-green-400 text-xs font-medium font-sans">+5.2%</span>
            </div>
          </div>
          
          <div className="space-y-2 lg:space-y-1.5">
            <div className="flex items-center justify-between text-xs font-sans">
              <span className="text-slate-400">Goal: $50M</span>
              <span className="text-blue-400 font-medium">65%</span>
            </div>
            <div className="w-full bg-slate-700/50 rounded-full h-2 lg:h-1.5">
              <motion.div 
                className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-2 lg:h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                initial={{ width: isMobile ? "65%" : 0 }}
                animate={{ width: "65%" }}
                transition={isMobile ? { duration: 0 } : { duration: 1.5, delay: 0.5 }}
              />
            </div>
          </div>
        </MobileOptimizedMotion>

        {/* Reward Stats */}
        <MobileOptimizedMotion 
          className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          variants={cardVariants}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          <div className="mb-4">
            <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-3 font-sans">
              REWARD STATS
            </h3>
            
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2 lg:mb-1.5">
                  <span className="text-slate-300 text-xs font-sans">APR</span>
                  <span className="text-xl lg:text-xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent font-sans">
                    {animatedValues.apr.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-slate-700/50 rounded-full h-1.5 lg:h-1">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 to-blue-400 h-1.5 lg:h-1 rounded-full shadow-md shadow-blue-500/50"
                    initial={{ width: isMobile ? "45%" : 0 }}
                    animate={{ width: "45%" }}
                    transition={isMobile ? { duration: 0 } : { duration: 1.5, delay: 0.7 }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2 lg:mb-1.5">
                  <span className="text-slate-300 text-xs font-sans">Rewards Multiplier</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent font-sans">1.5x</span>
                    <MobileOptimizedMotion 
                      className="px-1.5 py-0.5 rounded text-blue-400 text-xs font-medium bg-gradient-to-r from-blue-500/20 to-blue-600/20 border border-blue-500/30 font-sans"
                      whileHover={{ scale: 1.1, boxShadow: "0 0 10px rgba(59, 130, 246, 0.5)" }}
                    >
                      BOOST
                    </MobileOptimizedMotion>
                  </div>
                </div>
                <div className="w-full bg-slate-700/50 rounded-full h-1.5 lg:h-1">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 lg:h-1 rounded-full shadow-md shadow-blue-500/50"
                    initial={{ width: isMobile ? "75%" : 0 }}
                    animate={{ width: "75%" }}
                    transition={isMobile ? { duration: 0 } : { duration: 1.5, delay: 0.9 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </MobileOptimizedMotion>

        {/* RO Token */}
        <MobileOptimizedMotion 
          className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          variants={cardVariants}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          <div className="mb-3">
            <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2 lg:mb-1 font-sans">
              RO TOKEN
            </h3>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-xl lg:text-2xl font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent font-sans">
                ${animatedValues.price.toFixed(3)}
              </span>
              <span className="text-red-400 text-xs font-medium font-sans">-2.3%</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-xs font-sans">Market Cap</span>
              <span className="text-white text-sm font-medium font-sans">$87.5M</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-xs font-sans">Circulating Supply</span>
              <span className="text-white text-sm font-medium font-sans">100M RO</span>
            </div>
          </div>
        </MobileOptimizedMotion>
      </MobileOptimizedMotion>

      {/* Proof of Reserve Section */}
      <MobileOptimizedMotion 
        className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        transition={{ delay: 0.4 }}
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 lg:gap-0 mb-4">
          <h2 className="text-lg font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent font-sans">Proof of Reserve</h2>
          <div className="flex items-center gap-2">
            <motion.div 
              className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"
              animate={isMobile ? {} : { scale: [1, 1.2, 1] }}
              transition={isMobile ? {} : { duration: 2, repeat: Infinity }}
            />
            <span className="text-blue-400 font-mono text-sm font-sans truncate">
              Total reserve: ${animatedValues.reserve.toLocaleString()}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { title: 'Custody', amount: '$12,291,097', percentage: '50%' },
            { title: 'On-chain Custody', amount: '$7,374,658', percentage: '30%' },
            { title: 'Binance', amount: '$3,687,329', percentage: '15%' },
            { title: 'Fireblocks', amount: '$1,229,110', percentage: '5%' }
          ].map((item, i) => (
            <MobileOptimizedMotion 
              key={i}
              className="p-3 lg:p-3 rounded-lg bg-gradient-to-br from-slate-900/50 to-blue-900/20 border border-blue-500/20 hover:border-blue-400/40 transition-all duration-300"
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 0 20px rgba(59, 130, 246, 0.2)"
              }}
              initial={{ opacity: isMobile ? 1 : 0, y: isMobile ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={isMobile ? { duration: 0 } : { delay: 0.6 + i * 0.1 }}
            >
              <h4 className="text-slate-400 text-xs font-medium mb-1 uppercase tracking-wide font-sans">
                {item.title}
              </h4>
              <div className="text-base lg:text-lg font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent mb-0.5 font-sans">{item.amount}</div>
              <div className="text-xs text-slate-400 font-sans">{item.percentage} of total</div>
            </MobileOptimizedMotion>
          ))}
        </div>
      </MobileOptimizedMotion>

      {/* Portfolio Performance Chart Placeholder */}
      <MobileOptimizedMotion 
        className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        transition={{ delay: 0.6 }}
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 lg:gap-0 mb-4">
          <h2 className="text-lg font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent font-sans">Portfolio Performance</h2>
          <div className="flex items-center gap-1 overflow-x-auto">
            <MobileOptimizedMotion 
              className="px-3 py-1.5 lg:px-2 lg:py-1 rounded text-blue-400 text-xs font-medium bg-gradient-to-r from-blue-500/20 to-blue-600/20 border border-blue-500/30 font-sans whitespace-nowrap touch-manipulation"
              whileHover={{ scale: 1.1, boxShadow: "0 0 10px rgba(59, 130, 246, 0.5)" }}
            >
              24H
            </MobileOptimizedMotion>
            <MobileOptimizedMotion 
              className="px-3 py-1.5 lg:px-2 lg:py-1 rounded text-slate-400 text-xs font-medium hover:text-white transition-colors font-sans whitespace-nowrap touch-manipulation"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(59, 130, 246, 0.1)" }}
            >
              7D
            </MobileOptimizedMotion>
            <MobileOptimizedMotion 
              className="px-3 py-1.5 lg:px-2 lg:py-1 rounded text-slate-400 text-xs font-medium hover:text-white transition-colors font-sans whitespace-nowrap touch-manipulation"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(59, 130, 246, 0.1)" }}
            >
              30D
            </MobileOptimizedMotion>
          </div>
        </div>
        
        {/* Chart placeholder */}
        <div className="h-40 lg:h-32 rounded-lg bg-gradient-to-br from-slate-900/50 to-blue-900/20 border border-blue-500/20 flex items-center justify-center">
          <div className="text-center">
            <motion.svg 
              className="w-8 h-8 text-blue-400 mx-auto mb-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              animate={isMobile ? { opacity: 1 } : { opacity: [0.5, 1, 0.5] }}
              transition={isMobile ? { duration: 0 } : { duration: 2, repeat: Infinity }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </motion.svg>
            <div className="text-slate-400 text-sm font-sans">Performance chart coming soon</div>
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Recent Activity */}
      <MobileOptimizedMotion 
        className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        transition={{ delay: 0.8 }}
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 lg:gap-0 mb-4">
          <h2 className="text-lg font-semibold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent font-sans">Recent Activity</h2>
          <MobileOptimizedMotion 
            className="text-blue-400 hover:text-blue-300 transition-colors text-xs font-medium font-sans self-start sm:self-auto touch-manipulation"
            whileHover={{ scale: 1.05 }}
          >
            View All
          </MobileOptimizedMotion>
        </div>
        
        <div className="space-y-3 lg:space-y-2">
          {[
            { desc: 'Deposit to Vault', time: '2 hours ago', amount: '+2,500 RLUSD', value: '$2,500.00', type: 'deposit' },
            { desc: 'XRP → RLUSD Swap', time: '5 hours ago', amount: '+1,000 RLUSD', value: '$1,000.00', type: 'swap' },
            { desc: 'Bridge to Ethereum', time: '1 day ago', amount: '-500 RLUSD', value: '$500.00', type: 'bridge' },
            { desc: 'Vault Rewards', time: '2 days ago', amount: '+15.75 RLUSD', value: '$15.75', type: 'reward' }
          ].map((item, i) => (
            <MobileOptimizedMotion 
              key={i}
              className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-br from-slate-900/50 to-blue-900/20 border border-blue-500/20 hover:border-blue-400/40 transition-all duration-300 min-h-[60px] lg:min-h-0"
              whileHover={{ 
                scale: 1.01,
                boxShadow: "0 0 20px rgba(59, 130, 246, 0.2)"
              }}
              initial={{ opacity: isMobile ? 1 : 0, x: isMobile ? 0 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={isMobile ? { duration: 0 } : { delay: 1 + i * 0.1 }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <MobileOptimizedMotion 
                  className="w-10 h-10 lg:w-8 lg:h-8 rounded-lg bg-gradient-to-br from-slate-700/50 to-blue-900/30 border border-blue-500/30 flex items-center justify-center flex-shrink-0"
                  whileHover={{ scale: 1.1, boxShadow: "0 0 15px rgba(59, 130, 246, 0.3)" }}
                >
                  {item.type === 'deposit' && (
                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  )}
                  {item.type === 'swap' && (
                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  )}
                  {item.type === 'bridge' && (
                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                  {item.type === 'reward' && (
                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  )}
                </MobileOptimizedMotion>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-white text-sm font-sans truncate">{item.desc}</div>
                  <div className="text-xs text-slate-400 font-sans">{item.time}</div>
                </div>
              </div>
              
              <div className="text-right flex-shrink-0">
                <div className={`font-semibold text-sm font-sans ${item.amount.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                  {item.amount}
                </div>
                <div className="text-xs text-slate-400 font-sans">{item.value}</div>
              </div>
            </MobileOptimizedMotion>
          ))}
        </div>
      </MobileOptimizedMotion>
    </div>
  );
};

export default Dashboard; 